import { forwardRef, ForwardedRef, ReactNode } from 'react';
import { StyledBody, StyledDrawer, StyledFooter, StyledHeader } from './Drawer.styled';
import { DRAWER_SIZES } from './constants';
import { DrawSize } from './types';

type Props = {
  /* Drawer content */
  children?: ReactNode;
  /**
   * Drawer width
   *
   * This can be one of the following:
   *
   * 1. Draw width variant: `DRAWER_SIZES.MEDIUM | DRAWER_SIZES.LARGE`
   * 2. The numeric pixel width (which will be converted to rem)
   * 3. A valid css width string.  EG: `100%` or `calc(100vw - 20px)`
   */
  size?: DrawSize;
};

export const Drawer = ({ children, size = DRAWER_SIZES.SMALL, ...props }: Props) => (
  <StyledDrawer data-test="drawer" size={size} {...props}>
    {children}
  </StyledDrawer>
);

type HeaderProps = {
  /* Drawer header content */
  children?: ReactNode;
};

export const DrawerHeader = ({ children, ...props }: HeaderProps) => (
  <StyledHeader data-test="drawer-header" {...props}>
    {children}
  </StyledHeader>
);

type BodyProps = {
  /* Drawer body content */
  children?: ReactNode;
  /** Whether content inside is full width and height */
  noPadding?: boolean;
};

export const DrawerBody = forwardRef(
  ({ children, ...props }: BodyProps, ref: ForwardedRef<HTMLDivElement>) => (
    <StyledBody ref={ref} data-test="drawer-body" {...props}>
      {children}
    </StyledBody>
  ),
);

type FooterProps = {
  /* Drawer footer content */
  children?: ReactNode;
  alignRight?: boolean;
};

export const DrawerFooter = ({ children, alignRight = false, ...props }: FooterProps) => (
  <StyledFooter data-test="drawer-footer" alignRight={alignRight} {...props}>
    {children}
  </StyledFooter>
);
